import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { label: 'Loan Origination & Servicing', href: '/services#loan-origination' },
    { label: 'Lease & Data Administration', href: '/services#lease-administration' },
    { label: 'Consulting & Tech Solutions', href: '/services#consulting' },
    { label: 'GCC - Hire in India', href: '/gcc-hire-india' }
  ];

  const navItems = [
    { label: 'Home', href: '/', hasDropdown: false },
    { label: 'Services', href: '/services', hasDropdown: true, dropdown: services },
    { label: 'Project Showcase', href: '/case-studies', hasDropdown: false },
    { label: 'About', href: '/about', hasDropdown: false }
  ];

  const handleMouseEnter = (itemLabel) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    const item = navItems.find(i => i.label === itemLabel);
    if (item?.hasDropdown && item?.dropdown && item.dropdown.length > 0) {
      setActiveDropdown(itemLabel);
    }
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
    setDropdownTimeout(timeout);
  };

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
  };

  const handleDropdownMouseLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
    setDropdownTimeout(timeout);
  };

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-md border-b border-gray-100' 
          : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <img 
                src="/integrand-logo.png" 
                alt="Integrand Analytics" 
                className="h-10 w-auto transform transition-transform group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                {item.hasDropdown && item.dropdown && item.dropdown.length > 0 ? (
                  <>
                    <Link
                      to={item.href}
                      className={`transition-colors duration-200 text-sm font-medium flex items-center gap-1.5 group whitespace-nowrap py-2 ${
                        isActive(item.href) 
                          ? 'text-[#005B63]' 
                          : 'text-gray-600 hover:text-[#005B63]'
                      }`}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                    </Link>
                    
                    {/* Dropdown menu */}
                    {activeDropdown === item.label && (
                      <div 
                        className="absolute top-full left-0 pt-2"
                        onMouseEnter={handleDropdownMouseEnter}
                        onMouseLeave={handleDropdownMouseLeave}
                      >
                        <div className="w-64 bg-white border border-gray-200 rounded-lg shadow-xl py-2">
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.label}
                              to={dropdownItem.href}
                              className="block px-4 py-3 text-gray-600 hover:text-[#005B63] hover:bg-[#005B63]/5 transition-colors duration-200 text-sm"
                            >
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`transition-colors duration-200 text-sm font-medium flex items-center gap-1.5 group whitespace-nowrap ${
                      isActive(item.href) 
                        ? 'text-[#005B63]' 
                        : 'text-gray-600 hover:text-[#005B63]'
                    }`}
                  >
                    <span>{item.label}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer">
              <Button
                className="bg-[#005B63] hover:bg-[#007A85] text-white border-0 transition-all duration-300 group"
              >
                Schedule a Demo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-gray-700 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.hasDropdown && item.dropdown && item.dropdown.length > 0 ? (
                  <div>
                    <div className={`flex items-center justify-between text-base font-medium py-2 ${
                      isActive(item.href) ? 'text-[#005B63]' : 'text-gray-700'
                    }`}>
                      {item.label}
                      <ChevronDown className="w-4 h-4" />
                    </div>
                    <div className="pl-4 space-y-2 mt-2">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.label}
                          to={dropdownItem.href}
                          className="block text-gray-500 hover:text-[#005B63] transition-colors duration-200 text-sm py-2"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {dropdownItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center justify-between transition-colors duration-200 text-base font-medium py-2 ${
                      isActive(item.href) ? 'text-[#005B63]' : 'text-gray-700 hover:text-[#005B63]'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-[#005B63] hover:bg-[#007A85] text-white border-0">
                Schedule a Demo
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
